// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import { jsx } from 'panel';
import { ParamComponent } from 'panel';
import { VNode } from 'snabbdom';
import { IronContextRegistry } from 'common/report/context-registry';
import { LENGTH, TEXT_HIGHLIGHT_THEME, TextHighlightEl } from 'common/components/text-highlight';
import { defineAndMakeJsxFactory } from 'common/util/jsx';
import { panel2react } from 'common/util/react';
import { Translatable } from 'common/i18n';
import css from './index.inline.styl';
export enum TileImageId {
  ADD_REPORT = "add-report",
  START_FROM_TEMPLATE = "start-from-template",
  COMPANY_KPIS = "company-kpis",
  FEATURE_LAUNCH = "feature-launch",
  MARKETING_KPIS = "marketing-kpis",
  LIFECYCLE_COHORT_ANALYSIS = "lca",
  ECOMMERCE = "ecommerce",
  SAAS_COMPANY_KPI = "saas-company-kpi",
}
export enum TileTheme {
  // if theme needs to be altered, double check that it doesn't break in other usages
  PADDED_IMAGE = "padded-image",
  // ex: in empty-hero
  FULL_IMAGE = "full-image",
  // ex: in boards-template-modal
  NO_IMAGE = "no-image",

  // ex: in rhs-panel
}
export type TileType = {
  imageId?: TileImageId;
  title?: Translatable | VNode;
  description?: Translatable | VNode;
  theme?: TileTheme;
  selected?: boolean;
  fillWidth?: boolean;
  titleBadge?: Translatable;
};
type TileJsxProps = {
  params: TileType;
};
export class Tile extends ParamComponent<TileType, EmptyObj, IronContextRegistry> {
  get config() {
    return {
      useShadowDom: true,
      css,
      template: () => {
        const themeClass = this.params.theme || TileTheme.PADDED_IMAGE; // default to padded image theme
        return <div class={{
          selected: this.params.selected,
          'fill-width': this.params.fillWidth
        }} sel={".mp-tile-wrapper.".concat(themeClass)}>
            <div sel={".mp-tile-top.".concat(themeClass)}>
              <div sel={"#".concat(this.params.imageId, ".mp-tile-image.").concat(themeClass)} />
            </div>
            <div sel={".mp-tile-bottom.".concat(themeClass)}>
              <div sel={".mp-tile-title.".concat(themeClass)}>
                {this.renderText(this.params.title)}
                {this.params.titleBadge && <TextHighlightEl sel={".mp-tile-title-badge"} attrs={{
                text: this.print(this.params.titleBadge),
                length: LENGTH.WIDE,
                theme: TEXT_HIGHLIGHT_THEME.INFORMATION
              }} />}
              </div>
              <div sel={".mp-tile-description.".concat(themeClass)}>
                {this.renderText(this.params.description)}
                <div>
                  <slot />
                </div>
              </div>
            </div>
          </div>;
      },
      params: {
        imageId: {
          type: String,
          required: false
        },
        title: {
          type: String,
          required: false
        },
        description: {
          type: String,
          required: false
        },
        theme: {
          type: String,
          required: false
        },
        selected: {
          type: Boolean,
          required: false
        },
        fillWidth: {
          type: Boolean,
          required: false
        },
        titleBadge: {
          type: String,
          required: false
        }
      },
      contexts: ["i18n" as const]
    };
  }
  renderText(content: Translatable | VNode) {
    return typeof content === "function" ? this.print(content) : content;
  }
  get print() {
    return this.getContext("i18n").print;
  }
}
export const TileEl = defineAndMakeJsxFactory<TileJsxProps>("mp-tile", Tile);
export const TileReact = panel2react<TileJsxProps>("mp-tile", Tile);